<template>
    <div>
        <h3 class="mb-4 text-lg font-semibold dark:text-white">Write a Review</h3>

        <!-- <div v-if="hasReviewed" class="p-4 mb-4 text-yellow-700 bg-yellow-100 rounded-lg dark:bg-yellow-900 dark:text-yellow-200">
            <p>You have already reviewed this business. You can update your existing review below.</p>
        </div> -->

        <form @submit.prevent="submitReview" class="space-y-4">
            <div>
                <label for="rating" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Rating</label>
                <div class="flex items-center mt-1">
                    <button
                        v-for="star in 5"
                        :key="star"
                        type="button"
                        @click="form.rating = star"
                        class="focus:outline-none"
                    >
                        <StarIcon
                            :class="[
                                'h-8 w-8',
                                star <= form.rating ? 'text-yellow-400' : 'text-gray-300'
                            ]"
                        />
                    </button>
                </div>
                <p v-if="errors.rating" class="mt-1 text-sm text-red-600 dark:text-red-400">
                    {{ errors.rating }}
                </p>
            </div>

            <div class="p-2">
                <label for="comment" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Comment</label>
                <textarea
                    id="comment"
                    v-model="form.comment"
                    rows="4"
                    class="block p-3 mt-1 w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    :placeholder="hasReviewed ? 'Update your review...' : 'Share your experience...'"
                ></textarea>
                <p v-if="errors.comment" class="mt-1 text-sm text-red-600 dark:text-red-400">
                    {{ errors.comment }}
                </p>
            </div>

            <div class="flex justify-end">
                <div class="flex space-x-4">
                    <button
                        v-if="existingReview"
                        type="button"
                        @click="$emit('cancel')"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        :disabled="processing"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md border border-transparent transition-colors duration-150 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        {{ existingReview ? 'Update Review' : 'Submit Review' }}
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { StarIcon } from '@heroicons/vue/24/solid';
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useReviewStore } from '@/stores/reviewStore';
import { useToast } from 'primevue/usetoast'

const toast = useToast();
const authStore = useAuthStore();
const reviewStore = useReviewStore();

const props = defineProps({
    business: {
        type: Object,
        required: true
    },
    existingReview: {
        type: Object,
        default: null
    }
});

const hasReviewed = computed(() => props.existingReview !== null);
const errors = ref({});

// Initialize form with existing review data or defaults
const form = ref({
    rating: props.existingReview?.rating || 5,
    comment: props.existingReview?.comment || '',
});

// Update form when existingReview changes
watch(() => props.existingReview, (newReview) => {
    if (newReview) {
        form.value.rating = newReview.rating;
        form.value.comment = newReview.comment;
    }
}, { immediate: true });

const emit = defineEmits(['cancel', 'review-updated']);

// Use the loading state from the store
const processing = computed(() => reviewStore.submitting);

const submitReview = async () => {
    // Validate form
    errors.value = {};
    if (!form.value.rating) {
        errors.value.rating = 'Please select a rating';
        return;
    }
    if (!form.value.comment.trim()) {
        errors.value.comment = 'Please enter a comment';
        return;
    }

    try {
        let updatedReview;

        if (hasReviewed.value) {
            // Update existing review using the store
            updatedReview = await reviewStore.updateReview(props.existingReview.id, props.business.uuid, form.value);
        } else {
            // Create new review using the store
            updatedReview = await reviewStore.createReview(props.business.uuid, form.value);
            // Reset form after successful submission
            form.value.rating = 5;
            form.value.comment = '';
        }

        emit('review-updated', updatedReview);
    } catch (error) {
        console.error('Error submitting review:', error);
        if (error.response?.data?.errors) {
            errors.value = error.response.data.errors;
        } else if (reviewStore.error) {
            toast.add({
                title: 'Error',
                body: reviewStore.error,
                type: 'error',
                duration: 5000,
            });
        } else {
            toast.add({
                title: 'Error',
                body: 'Failed to submit review. Please try again.',
                type: 'error',
                duration: 5000,
            });
        }
    }
};
</script>
